import { ALLERGY_BASE_URL } from "@qendo/medical";
import { AllergyLevel, AllergyLevelMap } from "@qendo/medical/allergy";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { type RadioOption, RadioSelector } from "~/components/form/radio/RadioSelector";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import type { Allergy } from "~/entities/medical/allergy";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";

type AllergyFormProps = {
    readonly allergy?: Allergy;
    onRequestClose: () => void;
} & FullPageModalProps;

const AllergyLevelOptions: RadioOption<AllergyLevel>[] = Object.values(AllergyLevel).map((value) => ({
    value,
    label: AllergyLevelMap[value],
}));

export const AllergyForm = ({ allergy, onRequestClose, ...modalProps }: AllergyFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(allergy?.option_id ?? null);
    const [level, setLevel] = useState<AllergyLevel | null>(allergy?.level ?? null);
    const [flag, setFlag] = useState(allergy?.flag ?? false);
    const [description, setDescription] = useState(allergy?.description ?? "");
    const [photographs, setPhotographs] = useState<string[]>(allergy?.photograph_ids ?? []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    const showFlag = level === AllergyLevel.known;
    const canSave = canUpdateMedical(MedicalType.allergy) && !saving && option !== null && AllergyLevel !== null;

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (allergy?.option_id ?? null) ||
                flag !== (allergy?.flag ?? false) ||
                description !== (allergy?.description ?? "") ||
                level !== (allergy?.level ?? null) ||
                !elementsMatch(photographs, allergy?.photograph_ids ?? []),
        );
    }, [allergy, option, description, flag, photographs, level]);

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.allergy,
            id: allergy?.id,
            option_id: option,
            photograph_ids: photographs,
            flag,
            description,
            level: level as AllergyLevel,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${ALLERGY_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${allergy?.id ? "Edit" : "Add"} allergy`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.allergies}
                    value={option}
                    onItemSelected={setOption}
                    label="Allergy"
                    disabled={is_saving}
                    name="option_id"
                />
                <RadioSelector<AllergyLevel>
                    title="Is this a known or suspected allergy, or a sensitivity?"
                    options={AllergyLevelOptions}
                    disabled={is_saving}
                    value={level}
                    onValueChange={setLevel}
                />
                {showFlag && (
                    <CheckboxInput
                        value={flag}
                        onValueChange={setFlag}
                        label="Causes an emergency reaction"
                        disabled={is_saving}
                    />
                )}
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
